"use client";

/**
 * P7-14B · "Subscribe & save" on the PDP. Renders nothing unless the resolved
 * variant offers active plans (server-derived; flag off → empty list). The CTA
 * routes to the dedicated subscribe lane — deliberately NOT the cart (the
 * P7-14 split's no-mixed-carts decision); signed-out shoppers pass through
 * sign-in.
 */
import { LocaleLink, useTranslator } from "~/i18n/provider";
import { formatMoney } from "~/lib/money";
import { api } from "~/trpc/react";

const INTERVAL_LABEL: Record<string, string> = {
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
};

export function intervalLabel(unit: string, count: number): string {
  const word = INTERVAL_LABEL[unit] ?? unit.toLowerCase();
  return count === 1 ? `every ${word}` : `every ${count} ${word}s`;
}

export function SubscribeUpsell({
  variantId,
  currency,
  signedIn,
}: {
  variantId: string | null;
  currency: string;
  signedIn: boolean;
}) {
  const t = useTranslator();
  const plans = api.subscriptions.variantPlans.useQuery(
    { variantId: variantId ?? "" },
    { enabled: !!variantId, staleTime: 60_000 },
  );
  if (!variantId || !plans.data || plans.data.length === 0) return null;

  return (
    <div className="mt-4 rounded-lg border border-border bg-surface-2/40 p-3">
      <p className="text-sm font-semibold text-fg">{t("pdp.subscribeSave")}</p>
      <ul className="mt-2 grid gap-1.5">
        {plans.data.map((plan) => {
          const href = signedIn
            ? `/subscribe/${variantId}?plan=${plan.id}`
            : `/sign-in?callbackUrl=${encodeURIComponent(`/subscribe/${variantId}?plan=${plan.id}`)}`;
          return (
            <li key={plan.id}>
              <LocaleLink
                href={href}
                className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm text-fg hover:bg-surface-2"
              >
                <span>
                  {plan.name}
                  <span className="ml-2 text-xs text-fg-muted">
                    {intervalLabel(plan.intervalUnit, plan.intervalCount)}
                  </span>
                </span>
                <span className="font-semibold">
                  {formatMoney(plan.priceMinor, currency)}
                  {plan.discountPercent > 0 ? (
                    <span className="ml-1.5 text-xs font-normal text-success">
                      save {plan.discountPercent}%
                    </span>
                  ) : null}
                </span>
              </LocaleLink>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
